import React from 'react';
import { useTranslation } from 'react-i18next';
import EventCard from './EventCard';
import Pagination from '../../common/Pagination';
import EmptyState from './EmptyState';

const EventsList = ({ events, loading, pagination, onPageChange, onRefresh }) => {
  const { t } = useTranslation();

  if (loading && events.length === 0) {
    return (
      <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-8">
        <div className="text-center">
          <svg className="animate-spin h-8 w-8 text-orange-500 mx-auto mb-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <p className="text-gray-600">{t("player.loadingEvents")}</p>
        </div>
      </div>
    );
  }

  if (!loading && events.length === 0) {
    return <EmptyState onRefresh={onRefresh} />;
  }

  return (
    <div className="space-y-8">
      {/* Events Grid */}
      <div className="bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden">
        {/* Header */}
        <div className="px-6 py-4 bg-gradient-to-r from-orange-500 to-orange-600">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-bold text-white">
              {t("player.yourEvents")}
            </h2>
            <div className="text-orange-100">
              {pagination.totalEvents > 0 && (
                <span className="text-sm">
                  {t("common.showingResults", {
                    start: ((pagination.currentPage - 1) * 10) + 1,
                    end: Math.min(pagination.currentPage * 10, pagination.totalEvents),
                    total: pagination.totalEvents
                  })}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Events List */}
        <div className="p-6">
          {loading ? (
            <div className="space-y-4">
              {[...Array(3)].map((_, index) => (
                <div key={index} className="animate-pulse">
                  <div className="flex items-center space-x-4 p-4 border border-gray-200 rounded-lg">
                    <div className="w-16 h-16 bg-gray-300 rounded-lg"></div>
                    <div className="flex-1 space-y-2">
                      <div className="h-4 bg-gray-300 rounded w-3/4"></div>
                      <div className="h-3 bg-gray-300 rounded w-1/2"></div>
                      <div className="h-3 bg-gray-300 rounded w-1/4"></div>
                    </div>
                    <div className="w-24 h-8 bg-gray-300 rounded"></div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {events.map((event) => (
                <EventCard 
                  key={event._id} 
                  event={event} 
                />
              ))}
            </div>
          )}
        </div>

        {/* Pagination */}
        {!loading && pagination.totalPages > 1 && (
          <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
            <Pagination
              currentPage={pagination.currentPage}
              totalPages={pagination.totalPages}
              onPageChange={onPageChange}
              hasNextPage={pagination.hasNextPage}
              hasPrevPage={pagination.hasPrevPage}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default EventsList; 